import ListCard from '@Components/ListCard/ListCard';
import AssetTypeModal from '@Components/Modals/AssetType/AssetTypeModal';
import DeleteAssetTypeModal from '@Components/Modals/DeleteAssetType/DeleteAssetTypeModal';
import AssetTypeServices from '@Services/AssetType.services.ts';
import { AssetType } from '@Types/Asset.types';
import { PaginatedResponse } from '@Types/Response.types';
import { PAGE_PROP, PAGE_SIZE, PAGINATED_DEFAULT, PATHS, SEARCH_PROP, TABLE_STRUCTURES } from '@Utils/constants';
import { handleRequestError } from '@Utils/request.utils';
import { Button, Icons, Pagination, SearchBox, SectionCard, useTopHat } from '@kg-internal/pv3-uikit';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

const AssetTypes = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [assetTypes, setAssetTypes] = useState<PaginatedResponse<AssetType>>(PAGINATED_DEFAULT);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [showAssetTypeModal, setShowAssetTypeModal] = useState<boolean>(false);
  const [showDeleteAssetTypeModal, setShowDeleteAssetTypeModal] = useState<boolean>(false);
  const [selectedAssetType, setSelectedAssetType] = useState<AssetType | undefined>(undefined);
  const currentPage = Number(searchParams.get(PAGE_PROP) ?? '1');
  const searchQuery = searchParams.get(SEARCH_PROP) ?? '';

  useTopHat({
    activeNav: 'nav-settings',
    activeTab: undefined,
    tabs: [],
    breadcrumbs: [
      { title: 'Settings', href: PATHS.SETTINGS, id: 'Settings' },
      {
        title: 'Asset Types',
        href: `${PATHS.ASSET_TYPES}?page=${1}`,
        id: 'Asset Types',
      },
    ],
  });

  const handleNewPage = (newPage: number, data: PaginatedResponse<AssetType>) => {
    if (newPage <= 0 || newPage > assetTypes.totalPages) {
      return;
    }
    const page = newPage > data.totalPages ? data.prevPage : newPage;
    if (page !== currentPage) {
      searchParams.set(PAGE_PROP, page?.toString() || '1');
      setSearchParams(searchParams);
    }
  };

  const getAssetTypes = (page?: number) => {
    setSelectedAssetType(undefined);
    setIsLoading(true);
    AssetTypeServices.searchAssetType({ search: searchQuery }, page || currentPage)
      .then(({ data }) => {
        setAssetTypes(data);
        setErrorMessage(undefined);
        handleNewPage(currentPage, data);
      })
      .catch((error) => {
        handleRequestError(error, setErrorMessage);
      })
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    getAssetTypes();
  }, [searchQuery, currentPage]);

  const handleSearchQueryChange = (newValue: string) => {
    searchParams.set(SEARCH_PROP, newValue);
    searchParams.set(PAGE_PROP, '1');
    if (!newValue) {
      searchParams.delete(SEARCH_PROP);
    }
    setSearchParams(searchParams);
  };

  const handleActionAssetType = (id: string, setModal: (show: boolean) => void) => {
    setSelectedAssetType(assetTypes.docs.find((doc) => doc.id === id));
    setModal(true);
  };

  const handleOnCloseAssetType = (setModal: (show: boolean) => void) => {
    setModal(false);
    setSelectedAssetType(undefined);
  };

  return (
    <div className='grid'>
      <div className='col-12 col-md-3'>
        <SectionCard>
          <SearchBox
            id='search-asset-types'
            fullWidth
            focusOnRender
            value={searchQuery}
            defaultValue={searchQuery}
            onChange={handleSearchQueryChange}
          />
          <Button
            type='primary'
            fullWidth
            className='button-with-icon'
            onClick={() => {
              setShowAssetTypeModal(true);
            }}>
            {Icons.Plus} Add new Asset Type
          </Button>
        </SectionCard>
      </div>
      <div className='col-12 col-md-9'>
        <ListCard<AssetType>
          title={'Asset Types'}
          structure={TABLE_STRUCTURES.ASSET_TYPES}
          data={assetTypes.docs}
          loading={isLoading}
          withActions={{
            edit: (id: string) => handleActionAssetType(id, setShowAssetTypeModal),
            delete: (id: string) => handleActionAssetType(id, setShowDeleteAssetTypeModal),
          }}
          error={errorMessage}
          searchEntered={!!searchQuery}
        />
      </div>
      <div className='col-12 content-middle content-right-md'>
        <Pagination
          count={assetTypes.totalDocs}
          perPage={PAGE_SIZE}
          currentPage={currentPage - 1}
          onChange={(page) => handleNewPage(page + 1, assetTypes)}
        />
      </div>
      <AssetTypeModal
        show={showAssetTypeModal}
        onClose={() => handleOnCloseAssetType(setShowAssetTypeModal)}
        onSuccess={() => getAssetTypes(1)}
        assetType={selectedAssetType}
      />
      <DeleteAssetTypeModal
        show={showDeleteAssetTypeModal}
        onClose={() => handleOnCloseAssetType(setShowDeleteAssetTypeModal)}
        onSuccess={() => getAssetTypes(1)}
        assetType={selectedAssetType}
      />
    </div>
  );
};

export default AssetTypes;
